import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SectionNode from "./Nodes/SectionNode";
import { getRevAllNodes } from "../../../redux/procedureRevisionSlice";
import { showSnackbar } from "../../../redux/snackbarSlice";
import { parseSnapshotJson } from "../../AuditTrail/ActionComponents/utils/fetchSnapshotContent";
import Data from "./Data"
/**
 * RenderRevisionNodes Component
 *
 * Renders hierarchical revision nodes from single API data structure
 *
 * @param {string} partRef - Part ID
 * @param {string} procedureRevRef - Procedure revision ID
 * @param {boolean} disabled - If true, all checkboxes will be disabled
 * @param {boolean} showCheckbox - If false, all checkboxes will be hidden
 * @param {string} partNumber - Part number for navigation URLs
 * @param {string} revision - Revision for navigation URLs
 * @param {array} preloadedData - Pre-loaded nodes data (to avoid re-fetching)
 * @param {function} onDataLoaded - Callback when data is loaded (to cache it)
 */
const RenderRevisionNodes = ({
  partRef,
  procedureRevRef,
  type = "",
  disabled = false,
  showCheckbox = true,
  partNumber = "",
  revision = "",
  preloadedData = null,
  refreshKey = 0,
  isLeader = false,
  onDataLoaded = null,
  customOnNavigate = null, // Custom navigation handler
  checkboxState = null, // Custom checkbox state
  enableOnlySections = false, // If true, only section checkboxes are enabled
  showOnlySectionCheckboxes = false, // If true, hide checkboxes from steps and all children
  checkboxLabelColor = null, // Optional color for checkbox labels
  showSectionCheckbox = true, // If false, hide section checkbox while keeping children checkboxes
  hideChildCheckboxes = false, // If true, hide child node (rich text, video, etc.) checkboxes
  forceShowChildHeadings = false, // If true, always show child headings
  hideSubstepCheckboxes = false, // If true, hide substep checkboxes (e.g., when copying a step)
  isLock = false,
  isRevisionLocked = false, // New prop to disable checkboxes if revision is locked,
  current_snapshot = null,
  magicUrl=false,
  /** When set, step/substep children with these `type` values hide only the heading row, not body content (e.g. training checkmark). */
  hideChildHeadingsForTypes = null,
  /**
   * Optional training indicator:
   * - `allStepsTrained`: show "Trained" tag on every non-deleted step.
   * - `trainedStepNumbers`: a Set<number> of global step numbers to tag.
   */
  trainedStepNumbers = null,
  partiallyTrainedStepNumbers = null,
  allStepsTrained = false,
  totalQtyToBuild = null,
  /** When set (audit-trail cancel → revision-submission), load this S3 JSON as the tree instead of GET nodes API. */
  auditTrailNewContentSnapshotUrl = null,
  main_entry=undefined,
  /** Document-only: open canvas stage in a separate browser window on stage click (not the child-node screen). */
  enableCanvasStagePopup = false,

}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [nodesData, setNodesData] = useState(
    Array.isArray(preloadedData) ? preloadedData : [],
  );

  // Get auth user for lock API calls
  const authUser = useSelector((state) => state.auth.user);

  // Get draft data from Redux (for preview of unsaved changes)
  const draftSection = useSelector((state) => state.preview.draftSection);
  const draftStep = useSelector((state) => state.preview.draftStep);
  const draftSubstep = useSelector((state) => state.preview.draftSubstep);
  const draftChild = useSelector((state) => state.preview.draftChild);

  // Don't merge draft data when in delete/copy mode or when preloadedData is explicitly provided
  const deleteMode = useSelector((state) => state.preview.deleteMode);
  const copyReplaceMode = useSelector((state) => state.preview.copyReplaceMode);
  const shouldMergeDraft =
    !auditTrailNewContentSnapshotUrl &&
    !deleteMode &&
    !copyReplaceMode &&
    (preloadedData === null || preloadedData === undefined);

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    const safeA = Number.isFinite(intA) ? intA : 0;
    const safeB = Number.isFinite(intB) ? intB : 0;
    return safeA - safeB;
  };

  const getSectionSortOrderRaw = (s) =>
    s?.sibling_order ??
    s?.detail?.sibling_order ??
    s?.procedure_tree?.sibling_order;

  useEffect(() => {
    // If preloaded data is provided (including empty arrays), use it and skip fetch
    // Empty arrays are valid (e.g., when all sections are deleted)
    if (preloadedData !== null && preloadedData !== undefined) {
      setNodesData(Array.isArray(preloadedData) ? preloadedData : []);
      return;
    }

    const fetchNodesFromApi = async () => {
      const result = await dispatch(
        getRevAllNodes({
          partId: partRef,
          revisionId: procedureRevRef,
          withoutToken: magicUrl,
        }),
      );
      if (result.payload) {
        const nextNodes = Array.isArray(result.payload) ? result.payload : [];
        setNodesData(nextNodes);
        if (onDataLoaded) {
          onDataLoaded(nextNodes);
        }
        return true;
      }
      return false;
    };

    const fetchNodes = async () => {
      if (auditTrailNewContentSnapshotUrl) {
        try {
          const response = await fetch(auditTrailNewContentSnapshotUrl, {
            method: "GET",
            headers: { Accept: "application/json" },
            mode: "cors",
            credentials: "omit",
          });
          if (!response.ok) {
            throw new Error(`Snapshot HTTP ${response.status}`);
          }
          let snapshotNodes = [];
          const data = await response.json();
          const snapVc = data?.version_content;
          const isBulkAuditSnapshot =
            main_entry || snapVc?.operation === "bulk_audit_trail";
          if (isBulkAuditSnapshot) {
            const mainEntryBulk =
              Boolean(main_entry) ||
              snapVc?.main_entry === true ||
              (typeof snapVc?.main_entry === "string" &&
                snapVc.main_entry.trim().toLowerCase() === "true");
            const previewTid =
              snapVc?.preview_tree_node_id != null &&
              String(snapVc.preview_tree_node_id).trim() !== ""
                ? String(snapVc.preview_tree_node_id).trim()
                : undefined;
            const parsed = parseSnapshotJson(data, "new", {
              mainEntryBulk,
              previewTreeNodeId: mainEntryBulk ? undefined : previewTid,
              bulkReviewFirstOriginalLastNew: true,
            });
            snapshotNodes = parsed.rows;
            setNodesData(snapshotNodes);
          } else {
            snapshotNodes = parseSnapshotJson(data, "new").rows;
            setNodesData(snapshotNodes);
          }
          if (onDataLoaded) {
            onDataLoaded(snapshotNodes);
          }
          return;
        } catch (err) {
          console.error(
            "[RenderRevisionNodes] auditTrailNewContentSnapshotUrl:",
            err,
          );
          dispatch(
            showSnackbar({
              message:
                "Could not load saved version snapshot; showing live revision data.",
              type: "warning",
            }),
          );
        }
      }

      try {
        // Always prefer API so document view stays in sync with latest nodes.
        // For locked/snapshot revisions, fall back to snapshot only if API fetch fails.

        if (isLock && current_snapshot) {
          const response = await fetch(current_snapshot);
          if (!response.ok) {
            throw new Error(`Failed to fetch snapshot: ${response.statusText}`);
          }
          const data = await response.json();
          const snapshotNodes = Array.isArray(data?.data) ? data.data : [];
          setNodesData(snapshotNodes);
          if (onDataLoaded) {
            onDataLoaded(snapshotNodes);
          }
        }
        else{  
          await fetchNodesFromApi();
        }
      } catch (error) {
        console.error("Error fetching revision nodes:", error);
        if (isLock && current_snapshot) {
          try {
            const response = await fetch(current_snapshot);
            if (!response.ok) return;
            const data = await response.json();
            const snapshotNodes = Array.isArray(data?.data) ? data.data : [];
            setNodesData(snapshotNodes);
            if (onDataLoaded) onDataLoaded(snapshotNodes);
          } catch (snapshotError) {
            console.error("Error fetching fallback snapshot nodes:", snapshotError);
          }
        }
      }
    };

    if (partRef && procedureRevRef) {
      fetchNodes();
    }
  }, [
    partRef,
    procedureRevRef,
    preloadedData,
    refreshKey,
    isLock,
    current_snapshot,
    dispatch,
    onDataLoaded,
    magicUrl,
    auditTrailNewContentSnapshotUrl,
    main_entry,
  ]);

  // Merge draft data into nodes if present
  // IMPORTANT: Don't merge draft data when preloadedData is provided (delete/copy preview) or in delete/copy mode
  let sectionsToRender = [...(Array.isArray(nodesData) ? nodesData : [])];

  // 1. Merge draft section
  if (shouldMergeDraft && draftSection) {
    if (draftSection.mode === "edit") {
      // Replace existing section with draft
      sectionsToRender = sectionsToRender.map((section) =>
        section.tree_node_id === draftSection.tree_node_id
          ? draftSection
          : section
      );
    } else {
      // Add mode - insert draft section
      sectionsToRender = [...sectionsToRender, draftSection];
    }
  }

  // 2. Merge draft step into the appropriate section
  if (shouldMergeDraft && draftStep) {
    sectionsToRender = sectionsToRender.map((section) => {
      // Find the section that contains this step (or should contain it)
      const sectionSteps = section.steps || [];

      if (draftStep.mode === "edit") {
        // Replace existing step with draft
        const hasStep = sectionSteps.some(
          (s) => s.tree_node_id === draftStep.tree_node_id
        );
        if (hasStep) {
          return {
            ...section,
            steps: sectionSteps.map((step) =>
              step.tree_node_id === draftStep.tree_node_id ? draftStep : step
            ),
          };
        }
      } else {
        // Add mode - add draft step to the correct section based on section_id_ref
        const isTargetSection =
          section.tree_node_id === draftStep.section_id_ref;

        if (isTargetSection) {
          const updatedSteps = [...sectionSteps, draftStep];
          // Sort steps by sibling_order
          updatedSteps.sort((a, b) => {
            const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
            const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
            const orderCompare = compareSiblingOrders(orderA, orderB);

            // If orders are equal, draft steps should appear BEFORE existing steps
            // This ensures new steps created "from" an existing step appear right after it
            if (orderCompare === 0) {
              if (a.isDraft && !b.isDraft) return -1; // Draft comes before
              if (!a.isDraft && b.isDraft) return 1; // Existing comes after
              return 0;
            }

            return orderCompare;
          });
          return {
            ...section,
            steps: updatedSteps,
          };
        }
      }

      return section;
    });
  }

  // 3. Merge draft substep into the appropriate step
  if (shouldMergeDraft && draftSubstep) {
    sectionsToRender = sectionsToRender.map((section) => {
      const sectionSteps = section.steps || [];

      return {
        ...section,
        steps: sectionSteps.map((step) => {
          const stepSubsteps = step.substeps || [];

          if (draftSubstep.mode === "edit") {
            // Replace existing substep with draft
            const hasSubstep = stepSubsteps.some(
              (ss) => ss.tree_node_id === draftSubstep.tree_node_id
            );
            if (hasSubstep) {
              return {
                ...step,
                substeps: stepSubsteps.map((substep) =>
                  substep.tree_node_id === draftSubstep.tree_node_id
                    ? draftSubstep
                    : substep
                ),
              };
            }
          } else {
            // Add mode - add draft substep to the correct step based on step_id_ref
            const isTargetStep = step.tree_node_id === draftSubstep.step_id_ref;
            if (isTargetStep) {
              const updatedSubsteps = [...stepSubsteps, draftSubstep];
              // Sort substeps by sibling_order
              updatedSubsteps.sort((a, b) => {
                const orderA =
                  a.sibling_order || a.procedure_tree?.sibling_order;
                const orderB =
                  b.sibling_order || b.procedure_tree?.sibling_order;
                const orderCompare = compareSiblingOrders(orderA, orderB);

                // If orders are equal, draft substeps should appear BEFORE existing substeps
                // This ensures new substeps created "from" an existing substep appear right after it
                if (orderCompare === 0) {
                  if (a.isDraft && !b.isDraft) return -1; // Draft comes before
                  if (!a.isDraft && b.isDraft) return 1; // Existing comes after
                  return 0;
                }

                return orderCompare;
              });
              return {
                ...step,
                substeps: updatedSubsteps,
              };
            }
          }

          return step;
        }),
      };
    });
  }

  // 4. Merge draft child (attachment, video, rich-text, etc.) into appropriate step or substep
  if (shouldMergeDraft && draftChild) {
    sectionsToRender = sectionsToRender.map((section) => {
      const sectionSteps = section.steps || [];

      return {
        ...section,
        steps: sectionSteps.map((step) => {
          // Check if this child belongs to this step
          if (draftChild.substep_id_ref) {
            // Child belongs to a substep
            const stepSubsteps = step.substeps || [];
            return {
              ...step,
              substeps: stepSubsteps.map((substep) => {
                if (substep.tree_node_id === draftChild.substep_id_ref) {
                  const substepChildren = substep.children || [];

                  if (draftChild.mode === "edit") {
                    // Replace existing child
                    // For canvas nodes, check multiple ID fields
                    const isMatchingChild = (child) => {
                      if (draftChild.type === "canva") {
                        return (
                          child.tree_node_id === draftChild.tree_node_id ||
                          child.detail?.canvas_tree_node_id === draftChild.tree_node_id ||
                          child.canvas_tree_node_id === draftChild.tree_node_id ||
                          child.tree_node_id === draftChild.detail?.canvas_tree_node_id ||
                          child.detail?.canvas_tree_node_id === draftChild.detail?.canvas_tree_node_id
                        );
                      }
                      return child.tree_node_id === draftChild.tree_node_id;
                    };
                    return {
                      ...substep,
                      children: substepChildren.map((child) =>
                        isMatchingChild(child)
                          ? draftChild
                          : child
                      ),
                    };
                  } else {
                    // Add mode - add draft child and sort
                    const updatedChildren = [...substepChildren, draftChild];
                    updatedChildren.sort((a, b) => {
                      const orderA =
                        a.sibling_order || a.procedure_tree?.sibling_order;
                      const orderB =
                        b.sibling_order || b.procedure_tree?.sibling_order;
                      const orderCompare = compareSiblingOrders(orderA, orderB);

                      if (orderCompare === 0) {
                        if (a.isDraft && !b.isDraft) return -1;
                        if (!a.isDraft && b.isDraft) return 1;
                        return 0;
                      }

                      return orderCompare;
                    });
                    return {
                      ...substep,
                      children: updatedChildren,
                    };
                  }
                }
                return substep;
              }),
            };
          } else if (step.tree_node_id === draftChild.step_id_ref) {
            // Child belongs directly to this step
            const stepChildren = step.children || [];

            if (draftChild.mode === "edit") {
              // Replace existing child
              // For canvas nodes, check multiple ID fields
              const isMatchingChild = (child) => {
                if (draftChild.type === "canva") {
                  return (
                    child.tree_node_id === draftChild.tree_node_id ||
                    child.detail?.canvas_tree_node_id === draftChild.tree_node_id ||
                    child.canvas_tree_node_id === draftChild.tree_node_id ||
                    child.tree_node_id === draftChild.detail?.canvas_tree_node_id ||
                    child.detail?.canvas_tree_node_id === draftChild.detail?.canvas_tree_node_id
                  );
                }
                return child.tree_node_id === draftChild.tree_node_id;
              };
              return {
                ...step,
                children: stepChildren.map((child) =>
                  isMatchingChild(child)
                    ? draftChild
                    : child
                ),
              };
            } else {
              // Add mode - add draft child and sort
              const updatedChildren = [...stepChildren, draftChild];
              updatedChildren.sort((a, b) => {
                const orderA =
                  a.sibling_order || a.procedure_tree?.sibling_order;
                const orderB =
                  b.sibling_order || b.procedure_tree?.sibling_order;
                const orderCompare = compareSiblingOrders(orderA, orderB);

                if (orderCompare === 0) {
                  if (a.isDraft && !b.isDraft) return -1;
                  if (!a.isDraft && b.isDraft) return 1;
                  return 0;
                }

                return orderCompare;
              });
              return {
                ...step,
                children: updatedChildren,
              };
            }
          }

          return step;
        }),
      };
    });
  }

  const sortedSections = [...sectionsToRender].sort((a, b) => {
    const orderA = getSectionSortOrderRaw(a);
    const orderB = getSectionSortOrderRaw(b);

    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;

    // Fallback: compare created_at
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA; // latest comes first
  });

  const onNavigate =
    customOnNavigate ||
    ((nodeType, nodeData, context) => {

      // Set flag to indicate we're navigating from Document page to node view
      // This helps us know to re-acquire lock when returning
      sessionStorage.setItem(`fromDocument_${procedureRevRef}`, 'true');
      
      // Store leadership state before navigating
      const tabId = sessionStorage.getItem('tab-id');
      if (isLeader) {
        sessionStorage.setItem(`wasLeader_${procedureRevRef}_${tabId}`, 'true');
      }

      // Navigate immediately for better user experience
      // Build navigation path based on node type
      // Indices are calculated from data in each view component, no state passing needed
      // URL Pattern: /view-{type}/{partnumber}/{revision}/{partId}/{revisionId}/...
      switch (nodeType) {
        case "section":
          navigate(
            `/view-section/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${nodeData.tree_node_id}`
          );
          break;
        case "step":
          navigate(
            `/view-step/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${context.sectionId}/${nodeData.tree_node_id}`
          );
          break;
        case "substep":
          navigate(
            `/view-substep/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${context.sectionId}/${context.stepId}/${nodeData.tree_node_id}`
          );
          break;
        case "rich-text":
        case "video":
        case "form":
        case "attachment":
          // For child nodes
          const substepIdParam = context.substepId || "";
          // Build URL with substep_id_ref as query parameter if it exists
          const baseUrl = `/view-childnode/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${context.sectionId}/${context.stepId}/${nodeData.tree_node_id}`;
          const urlWithQuery = substepIdParam
            ? `${baseUrl}?substep_id_ref=${substepIdParam}`
            : baseUrl;
          navigate(urlWithQuery);
          break;
        case "canva":
          // For canvas elements, use detail.canvas_tree_node_id instead of tree_node_id
          const canvasSubstepIdParam = context.substepId || "";
          // Use canvas_tree_node_id from detail if available, otherwise fall back to tree_node_id
          const canvasNodeId =
            nodeData.detail?.canvas_tree_node_id || nodeData.tree_node_id;
          const canvasBaseUrl = `/view-childnode/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${context.sectionId}/${context.stepId}/${canvasNodeId}`;
          const canvasUrlWithQuery = canvasSubstepIdParam
            ? `${canvasBaseUrl}?substep_id_ref=${canvasSubstepIdParam}`
            : canvasBaseUrl;
          navigate(canvasUrlWithQuery);
          break;
        default:
      }

      // Navigation complete - no lock API call
    });

  // Calculate total steps before each section for global step numbering
  const getGlobalStepStart = (sectionIndex) => {
    let totalSteps = 0;
    for (let i = 0; i < sectionIndex; i++) {
      totalSteps += sortedSections[i]?.steps?.length || 0;
    }
    return totalSteps;
  };

  return (
    <div>
      {/* Node section */}
      {sortedSections.map((section, index) => (
        <SectionNode
          key={section.tree_node_id}
          section={section}
          sectionIndex={index}
          documentType={type}
          documentPartNumber={partNumber}
          documentRevision={revision}
          globalStepStart={getGlobalStepStart(index)}
          onNavigate={onNavigate}
          disabled={disabled}
          showCheckbox={showCheckbox}
          showOnlySectionCheckboxes={showOnlySectionCheckboxes}
          checkboxLabelColor={checkboxLabelColor}
          allNodesData={sortedSections}
          showSectionCheckbox={showSectionCheckbox}
          hideChildCheckboxes={hideChildCheckboxes}
          forceShowChildHeadings={forceShowChildHeadings}
          hideSubstepCheckboxes={hideSubstepCheckboxes}
          checkboxState={
            checkboxState ||
            (enableOnlySections
              ? {
                allChildrenDisabled: true,
                allChildrenChecked: false,
              }
              : undefined)
          }
          isLock={isLock}
          isRevisionLocked={isRevisionLocked}
          hideChildHeadingsForTypes={hideChildHeadingsForTypes}
          trainedStepNumbers={trainedStepNumbers}
          partiallyTrainedStepNumbers={partiallyTrainedStepNumbers}
          allStepsTrained={allStepsTrained}
          totalQtyToBuild={totalQtyToBuild}
          enableCanvasStagePopup={enableCanvasStagePopup}
        />
      ))}
      {/* End Node section */}
    </div>
  );
};

export default RenderRevisionNodes;
